import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Text,
  Flex,
  IconButton,
  Divider,
} from "@chakra-ui/react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import PrimaryFormLabel from "../../../components/PrimaryFormLabel";
import Iframe from "react-iframe";
import PrimaryButton from "../../../components/PrimaryButton";

const FlightTicketDetailsModal = ({
  isOpen,
  onClose,
  flightDetails,
  patientName,
  attendantDetails = [],
  downloadFiles = () => {},
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allTickets, setAllTickets] = useState([]);

  useEffect(() => {
    // Patient ticket - keep all ticket images in an array
    const patientTicket = flightDetails?.flight_tickets
      ? [
          {
            ticket_images: flightDetails.flight_tickets,
            passenger_name: patientName,
            airline_details: flightDetails.airline_details,
            flight_number: flightDetails.flight_number,
            arrival_date: flightDetails.arrival_date,
            arrival_time: flightDetails.arrival_time,
            from_airport: flightDetails.from_airport,
            from_country: flightDetails.from_country,
            to_airport: flightDetails.to_airport,
            to_country: flightDetails.to_country,
            isPatient: true,
            travelling_on_same_flight: null,
          },
        ]
      : [];

    // Only include attendants who are NOT traveling with patient
    const attendantTickets = attendantDetails
      .filter(
        (attendant) => attendant.travelling_on_same_flight_as_patient === 0
      )
      .map((attendant) => ({
        ticket_images: attendant.flight_tickets || [],
        passenger_name: attendant.attendant_name,
        passport_no: attendant.passport_no,
        airline_details: attendant.airline_details,
        flight_number: attendant.flight_number,
        arrival_date: attendant.arrival_date,
        arrival_time: attendant.arrival_time,
        from_airport: attendant.from_airport,
        from_country: attendant.from_country,
        to_airport: attendant.to_airport,
        to_country: attendant.to_country,
        isPatient: false,
        travelling_on_same_flight: false,
      }));

    // Add attendants traveling with patient
    const accompanyingAttendants = attendantDetails
      .filter(
        (attendant) => attendant.travelling_on_same_flight_as_patient === 1
      )
      .map((attendant) => ({
        ticket_images: flightDetails?.flight_tickets || [],
        passenger_name: attendant.attendant_name,
        passport_no: attendant.passport_no,
        isPatient: false,
        travelling_on_same_flight: true,
      }));

    const validTickets = [
      ...patientTicket,
      ...attendantTickets,
      ...accompanyingAttendants,
    ].filter(
      (ticket) =>
        // Include if it has at least one ticket image or is an accompanying attendant
        ticket.ticket_images.length > 0 ||
        ticket.travelling_on_same_flight === true
    );

    setAllTickets(validTickets);
    setCurrentImageIndex(0); // Reset image index when tickets change
  }, [flightDetails, patientName, attendantDetails]);

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % allTickets.length);
    setCurrentImageIndex(0);
  };

  const handlePrevious = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + allTickets.length) % allTickets.length
    );
    setCurrentImageIndex(0);
  };

  const handleNextImage = () => {
    const currentTicket = allTickets[currentIndex];
    if (currentTicket?.ticket_images?.length > 1) {
      setCurrentImageIndex(
        (prev) => (prev + 1) % currentTicket.ticket_images.length
      );
    }
  };

  const handlePreviousImage = () => {
    const currentTicket = allTickets[currentIndex];
    if (currentTicket?.ticket_images?.length > 1) {
      setCurrentImageIndex(
        (prev) =>
          (prev - 1 + currentTicket.ticket_images.length) %
          currentTicket.ticket_images.length
      );
    }
  };

  const downloadFlightTickets = () => {
    let fileType = "flight_ticket";
    downloadFiles(fileType);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="primary.1" textAlign="center" fontSize="2xl">
          Flight Ticket Details
          {allTickets.length > 0 && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {currentIndex + 1} of {allTickets.length}
            </Text>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <PrimaryButton
            mb="20px"
            onClick={downloadFlightTickets}
            display={"flex"}
            justifyContent={"flex-end"}
            ml="auto"
          >
            Download Flight Tickets
          </PrimaryButton>
          {!allTickets.length ? (
            <Text>No flight tickets available</Text>
          ) : (
            <Box position="relative">
              <Flex justify="center" align="center">
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={handlePrevious}
                  isDisabled={allTickets.length <= 1}
                  position="absolute"
                  left={0}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={2}
                />

                <Box mx={12} width="100%">
                  {allTickets[currentIndex]?.ticket_images?.length > 0 && (
                    <>
                      <Box position="relative">
                        <Iframe
                          src={
                            allTickets[currentIndex].ticket_images[
                              currentImageIndex
                            ]
                          }
                          alt="Flight Ticket"
                          height="300px"
                          width="100%"
                        />
                        {allTickets[currentIndex].ticket_images.length > 1 && (
                          <>
                            <IconButton
                              icon={<ChevronLeftIcon />}
                              onClick={handlePreviousImage}
                              position="absolute"
                              left={2}
                              top="50%"
                              transform="translateY(-50%)"
                              zIndex={2}
                            />
                            <IconButton
                              icon={<ChevronRightIcon />}
                              onClick={handleNextImage}
                              position="absolute"
                              right={2}
                              top="50%"
                              transform="translateY(-50%)"
                              zIndex={2}
                            />
                            <Text fontSize="sm" textAlign="center" mt={2}>
                              Image {currentImageIndex + 1} of{" "}
                              {allTickets[currentIndex].ticket_images.length}
                            </Text>
                          </>
                        )}
                      </Box>
                      <Divider my={4} />
                    </>
                  )}

                  <Box>
                    <Text
                      fontWeight="bold"
                      fontSize="lg"
                      textAlign="center"
                      color="green.500"
                      mb={4}
                    >
                      {allTickets[currentIndex]?.isPatient
                        ? "Patient Details"
                        : "Attendant Details"}
                    </Text>

                    <Flex mb={2}>
                      <PrimaryFormLabel minW="120px">Name:</PrimaryFormLabel>
                      <Text>{allTickets[currentIndex]?.passenger_name}</Text>
                    </Flex>

                    <Flex mb={2}>
                      <PrimaryFormLabel minW="120px">
                        Passport No:
                      </PrimaryFormLabel>
                      <Text>
                        {allTickets[currentIndex]?.passport_no || "N/A"}
                      </Text>
                    </Flex>

                    {/* Show "Coming along with patient" message for accompanying attendants */}
                    {allTickets[currentIndex]?.travelling_on_same_flight ===
                      true && (
                      <Text
                        color="red.500"
                        fontSize="sm"
                        mt={2}
                        textAlign="center"
                      >
                        Coming along with patient
                      </Text>
                    )}

                    {/* Show flight details only for patient and separate-traveling attendants */}
                    {(allTickets[currentIndex]?.isPatient ||
                      allTickets[currentIndex]?.travelling_on_same_flight ===
                        false) && (
                      <>
                        <Flex mb={2}>
                          <PrimaryFormLabel minW="120px">
                            Airline:
                          </PrimaryFormLabel>
                          <Text>
                            {allTickets[currentIndex]?.airline_details || "N/A"}
                          </Text>
                        </Flex>

                        <Flex mb={2}>
                          <PrimaryFormLabel minW="120px">
                            Flight Number:
                          </PrimaryFormLabel>
                          <Text>
                            {allTickets[currentIndex]?.flight_number || "N/A"}
                          </Text>
                        </Flex>

                        <Flex mb={2}>
                          <PrimaryFormLabel minW="120px">
                            Arrival Date:
                          </PrimaryFormLabel>
                          <Text>
                            {allTickets[currentIndex]?.arrival_date || "N/A"}
                          </Text>
                        </Flex>

                        <Flex mb={2}>
                          <PrimaryFormLabel minW="120px">
                            Arrival Time:
                          </PrimaryFormLabel>
                          <Text>
                            {allTickets[currentIndex]?.arrival_time || "N/A"}
                          </Text>
                        </Flex>

                        <Flex mb={2}>
                          <PrimaryFormLabel minW="120px">
                            From:
                          </PrimaryFormLabel>
                          <Text>
                            {allTickets[currentIndex]?.from_airport}
                            {allTickets[currentIndex]?.from_country &&
                              ` (${allTickets[currentIndex]?.from_country})`}
                          </Text>
                        </Flex>

                        <Flex mb={2}>
                          <PrimaryFormLabel minW="120px">To:</PrimaryFormLabel>
                          <Text>
                            {allTickets[currentIndex]?.to_airport}
                            {allTickets[currentIndex]?.to_country &&
                              ` (${allTickets[currentIndex]?.to_country})`}
                          </Text>
                        </Flex>
                      </>
                    )}
                  </Box>
                </Box>

                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={handleNext}
                  isDisabled={allTickets.length <= 1}
                  position="absolute"
                  right={0}
                  top="50%"
                  transform="translateY(-50%)"
                  zIndex={2}
                />
              </Flex>
            </Box>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default FlightTicketDetailsModal;
